import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, Plus, Trash2, Instagram } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AmenitiesPicker from "@/components/AmenitiesPicker";
import HouseRulesAndAccess from "@/components/HouseRulesAndAccess";
import PositionPicker from "@/components/PositionPicker";
import { PhotoUploader, MultiPhotoUploader, absUrl } from "@/components/PhotoUploader";
import PushCard from "./PushCard";
import VerificationCard from "./VerificationCard";
import AICopyDialog from "./AICopyDialog";
import ConnectCard from "./ConnectCard";
import NoShowPenaltyPicker from "./NoShowPenaltyPicker";
import AssistantIACard from "./AssistantIACard";
import AlloggiatiCard from "./AlloggiatiCard";
import CityTaxCard from "./CityTaxCard";

export default function PropertyTab({ property, refresh }) {
  const { t, lang } = useLang();
  const IT = lang === "it";
  const [form, setForm] = useState(property);
  const [saving, setSaving] = useState(false);
  const [savingAmenities, setSavingAmenities] = useState(false);
  useEffect(() => setForm(property), [property]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const cleanEmail = (form.email || "").trim();
      const cleanPhone = (form.phone || "").trim();
      await api.put("/properties/me", {
        name: form.name, description_it: form.description_it, description_en: form.description_en,
        city: form.city, region: form.region, address: form.address,
        phone: cleanPhone || null,
        email: cleanEmail || null,
        amenities: Array.isArray(form.amenities) && form.amenities.length > 0 && !form.amenities_raw_dirty
          ? form.amenities
          : (form.amenities_raw ?? form.amenities.join(", ")).split(",").map(s => s.trim()).filter(Boolean),
        photos: (form.photos_raw ?? (form.photos || []).join("\n")).split("\n").map(s => s.trim()).filter(Boolean),
        hero_image: form.hero_image, property_type: form.property_type,
        instagram_handle: form.instagram_handle,
        instagram_posts: (form.instagram_posts || []).filter(p => p.image_url),
        ...(form.cin ? { cin: form.cin } : {}),
      });
      toast.success(t("dash.saved")); refresh();
    } catch (e) { console.error("property save failed", e); toast.error(errMsg(e, t("dash.toast_error"))); }
    finally { setSaving(false); }
  };

  // Dedicated save for amenities only — decouples from other field validation errors
  const saveAmenities = async () => {
    setSavingAmenities(true);
    try {
      const arr = Array.isArray(form.amenities) ? form.amenities : [];
      await api.put("/properties/me", { amenities: arr });
      toast.success(IT ? `Dotazioni salvate (${arr.length})` : `Amenities saved (${arr.length})`);
      refresh();
    } catch (e) { console.error("amenities save failed", e); toast.error(errMsg(e, t("dash.toast_error"))); }
    finally { setSavingAmenities(false); }
  };
  const applyAI = ({ description_it, description_en, amenities }) => {
    setForm((f) => ({ ...f, description_it, description_en,
      amenities: amenities || f.amenities,
      amenities_raw: (amenities || f.amenities).join(", ") }));
  };
  const addIgPost = () => set("instagram_posts", [...(form.instagram_posts || []), { image_url: "", caption: "", link: "" }]);
  const setIg = (i, k, v) => {
    const arr = [...(form.instagram_posts || [])];
    arr[i] = { ...arr[i], [k]: v };
    set("instagram_posts", arr);
  };
  const rmIg = (i) => set("instagram_posts", (form.instagram_posts || []).filter((_, x) => x !== i));
  const togglePublish = async () => {
    try {
      await api.put("/properties/me", { published: !property.published });
      toast.success(t("dash.saved")); refresh();
    } catch { toast.error(t("dash.toast_error")); }
  };
  const copyLink = () => {
    const url = `${window.location.origin}/p/${property.slug}`;
    navigator.clipboard.writeText(url); toast.success(t("dash.copied"));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t("dash.name")}</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="prop-name"/></div>
          <div><Label>{t("dash.property_type")}</Label>
            <Select value={form.property_type || "bnb"} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger data-testid="prop-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="bnb">B&B</SelectItem><SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="agriturismo">Agriturismo</SelectItem>
                <SelectItem value="affittacamere">Affittacamere</SelectItem>
                <SelectItem value="casa_vacanza">Casa vacanza</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t("dash.city")}</Label><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="prop-city"/></div>
          <div><Label>{t("dash.region")}</Label><Input value={form.region || ""} onChange={(e) => set("region", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>{t("dash.address")}</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)}/></div>
          <div><Label>{t("dash.phone")}</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}/></div>
          <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)}/></div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1">
              Codice CIN <span className="text-destructive">*</span>
            </Label>
            <Input
              value={form.cin || ""}
              onChange={(e) => set("cin", e.target.value.toUpperCase())}
              placeholder="IT058091C25010A5B"
              maxLength={25}
              className="uppercase font-mono tracking-wider"
              data-testid="prop-cin"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Codice Identificativo Nazionale MITUR (DL 145/2023). 17 caratteri, inizia con IT.</p>
          </div>
        </div>
        <div><Label>{t("dash.desc_it")}</Label><Textarea rows={3} value={form.description_it || ""} onChange={(e) => set("description_it", e.target.value)} data-testid="prop-desc-it"/></div>
        <div><Label>{t("dash.desc_en")}</Label><Textarea rows={3} value={form.description_en || ""} onChange={(e) => set("description_en", e.target.value)} data-testid="prop-desc-en"/></div>
        <div className="flex items-center justify-end"><AICopyDialog property={form} onApply={applyAI}/></div>
        <div className="border-t border-border pt-4 space-y-3">
          <AmenitiesPicker
            value={form.amenities || []}
            onChange={(arr) => setForm((f) => ({ ...f, amenities: arr, amenities_raw: arr.join(", ") }))}
          />
          <div className="flex justify-end">
            <Button
              onClick={saveAmenities}
              disabled={savingAmenities}
              variant="outline"
              size="sm"
              className="lift"
              data-testid="amenities-save-btn"
            >
              {savingAmenities ? (IT ? "Salvataggio…" : "Saving…") : (IT ? "Salva dotazioni" : "Save amenities")}
            </Button>
          </div>
        </div>

        <PositionPicker
          property={property}
          onSaved={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
        />

        <HouseRulesAndAccess
          property={property}
          onSave={async (patch) => {
            try {
              await api.put("/properties/me", patch);
              toast.success("Regole e istruzioni salvate");
              refresh();
            } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
          }}
        />
        <NoShowPenaltyPicker
          value={form.no_show_penalty || property?.no_show_penalty || "full"}
          onChange={async (v) => {
            set("no_show_penalty", v);
            try {
              await api.put("/properties/me", { no_show_penalty: v });
              toast.success(IT ? "Politica no-show aggiornata" : "No-show policy updated");
              refresh();
            } catch (e) { toast.error(errMsg(e, "Error")); }
          }}
        />
        <CityTaxCard property={property} onSaved={refresh}/>        <div><Label>{t("dash.hero_image")}</Label>
          <PhotoUploader value={form.hero_image} onChange={(u) => set("hero_image", u)} testid="hero-uploader"/>
        </div>
        <div><Label>{t("dash.photos")}</Label>
          <MultiPhotoUploader values={form.photos || []} onChange={(arr) => setForm((f) => ({ ...f, photos: arr, photos_raw: arr.join("\n") }))} testid="photos-uploader"/>
        </div>
        <div className="border-t border-border pt-4 space-y-3" data-testid="ig-section">
          <div className="flex items-center gap-2">
            <Instagram className="w-4 h-4 text-primary"/>
            <Label className="mb-0">{t("dash.instagram")}</Label>
          </div>
          <Input value={form.instagram_handle || ""} onChange={(e) => set("instagram_handle", e.target.value.replace(/^@/, ""))} placeholder="@yourhandle" data-testid="ig-handle"/>
          <div className="space-y-2">
            {(form.instagram_posts || []).map((p, i) => (
              <div key={p.image_url || p.link || `ig-${i}`} className="grid grid-cols-[80px_1fr_auto] gap-2 items-start" data-testid={`ig-post-${i}`}>
                <div className="w-20 h-20 rounded-md border border-border overflow-hidden bg-muted grid place-items-center">
                  {p.image_url ? <img src={absUrl(p.image_url)} className="w-full h-full object-cover"/> : <Instagram className="w-6 h-6 text-muted-foreground"/>}
                </div>
                <div className="space-y-1">
                  <Input placeholder={t("dash.ig_image")} value={p.image_url || ""} onChange={(e) => setIg(i, "image_url", e.target.value)}/>
                  <Input placeholder={t("dash.ig_caption")} value={p.caption || ""} onChange={(e) => setIg(i, "caption", e.target.value)}/>
                  <Input placeholder={t("dash.ig_link")} value={p.link || ""} onChange={(e) => setIg(i, "link", e.target.value)}/>
                </div>
                <button type="button" onClick={() => rmIg(i)} className="text-muted-foreground hover:text-destructive mt-2"><Trash2 className="w-4 h-4"/></button>
              </div>
            ))}
            {(form.instagram_posts || []).length < 6 && (
              <Button type="button" size="sm" variant="outline" onClick={addIgPost} className="lift" data-testid="ig-add-btn"><Plus className="w-3.5 h-3.5 mr-1"/>{t("dash.ig_add")}</Button>
            )}
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="prop-save-btn">{saving ? (IT ? "Salvataggio…" : "Saving…") : t("dash.save")}</Button>
      </div>

      <div className="space-y-4">
        <div className="border border-border rounded-lg p-5 bg-card">
          <div className="flex items-center justify-between mb-4">
            <span className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground">Vetrina</span>
            <span className={`text-xs px-2 py-1 rounded-full ${property.published ? "bg-sage text-white" : "bg-muted text-muted-foreground"}`} data-testid="publish-status">
              {property.published ? t("dash.published") : t("dash.not_published")}
            </span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm">Publish</span>
            <Switch checked={property.published} onCheckedChange={togglePublish} data-testid="publish-toggle"/>
          </div>
          <div className="flex flex-col gap-2">
            <Button asChild variant="outline" size="sm" className="lift" disabled={!property.published} data-testid="view-showcase-btn">
              <Link to={`/p/${property.slug}`} target="_blank">
                <ExternalLink className="w-3.5 h-3.5 mr-1"/> {t("dash.view_showcase")}
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={copyLink} className="lift" data-testid="copy-link-btn">
              <Copy className="w-3.5 h-3.5 mr-1"/> {t("dash.copy_link")}
            </Button>
          </div>
        </div>
        {property.hero_image && (
          <div className="rounded-lg overflow-hidden border border-border aspect-[4/3]">
            <img src={absUrl(property.hero_image)} className="w-full h-full object-cover"/>
          </div>
        )}
        <PushCard/>
        <VerificationCard/>
        <ConnectCard/>
        <AlloggiatiCard/>
        <AssistantIACard/>
      </div>
    </div>
  );
}
